import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../services/api";
import AffiliatesAdmin from "../components/AffiliatesAdmin";

type User = {
  id: number;
  email: string;
  role: string;
  plan: string;
  subscription_status: string;
  credits: number;
  status: string;
};
type Setting = { key: string; value: string };
type Coupon = {
  id: string;
  name: string | null;
  percent_off: number | null;
  amount_off: number | null;
  duration: string;
  active: boolean;
  promotion_codes: { id: string; code: string }[];
};
type ImportResult = {
  inserted: number;
  updated: number;
  skipped: number;
  failed: number;
  failures?: { row: number; email: string; error: string }[];
};
type ImportPreview = {
  headers: string[];
  preview: Record<string, string>[];
  totalRows: number;
  kind: string;
  duplicates: number;
  placeholders: number;
  existingRows: number;
  unmappedColumns: string[];
  validRows: number;
  failures: { row: number; email: string; error: string }[];
};
const csvCell = (value: unknown) => {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export default function AdminPage({ onLogout }: { onLogout: () => void }) {
  const [inventory,setInventory] = useState({contacts:0,companies:0});
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState("add_new_only");
  const [confirmation, setConfirmation] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<Setting[]>([]);
  const [history, setHistory] = useState<Record<string, unknown>[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponForm, setCouponForm] = useState({
    name: "",
    code: "",
    type: "percent",
    value: "10",
    duration: "once",
    durationMonths: "3",
    maxRedemptions: "",
    expiresOn: "",
  });
  const [result, setResult] = useState<ImportResult | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const logout = async () => {
    try {
      const response = await apiFetch("/api/auth/logout", { method: "POST" });
      if (!response.ok) {
        setMessage("Unable to sign out. Please try again.");
        return;
      }
      onLogout();
    } catch {
      setMessage("Unable to sign out. Please try again.");
    }
  };

  const loadAdminData = async () => {
    const [usersResponse, settingsResponse, historyResponse, couponsResponse, summaryResponse] =
      await Promise.all([
        apiFetch("/api/admin/users"),
        apiFetch("/api/admin/settings"),
        apiFetch("/api/admin/leads/import-history"),
        apiFetch("/api/admin/coupons"),
        apiFetch("/api/admin/leads/summary"),
      ]);
    if (summaryResponse.ok) setInventory(await summaryResponse.json());
    if (usersResponse.ok) setUsers((await usersResponse.json()).users);
    if (settingsResponse.ok)
      setSettings((await settingsResponse.json()).settings);
    if (historyResponse.ok) setHistory((await historyResponse.json()).history);
    if (couponsResponse.ok) setCoupons((await couponsResponse.json()).coupons);
  };

  useEffect(() => {
    void loadAdminData();
  }, []);

  const downloadTemplate = async (kind: string) => {
    try {
      const response = await apiFetch(`/api/admin/leads/template/${kind}`);
      if(!response.ok) throw new Error();
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement('a');a.href=url;a.download=`reachatlas-${kind}-template.csv`;a.click();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    } catch {setMessage('Unable to download the template. Please try again.');}
  };
  const importCsv = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) {
      setMessage("Choose a CSV file first.");
      return;
    }
    setBusy(true);
    setMessage("");
    setResult(null);
    const body = new FormData();
    body.append("file", file);
    body.append("mode", mode);
    if (mode === "replace_all") body.append("confirmation", confirmation);
    try {
    const response = await apiFetch(
      preview ? "/api/admin/leads/import" : "/api/admin/leads/import/preview",
      { method: "POST", body },
    );
    const payload = await response.json();
    setBusy(false);
    if (!response.ok) {
      setMessage(payload.error || "Import failed.");
      return;
    }
    if (!preview) {
      setPreview(payload);
      setMessage(
        `Preview ready: ${payload.validRows} valid of ${payload.totalRows} rows.`,
      );
      return;
    }
    setResult(payload);
    setPreview(null);
    setMessage("Import completed.");
    await loadAdminData();
    } catch {setMessage('Upload interrupted. Please retry the preview before importing.');setPreview(null);}
    finally {setBusy(false);}
  };

  const adjustCredits = async (user: User) => {
    const amountText = window.prompt(
      `Credit adjustment for ${user.email}`,
      "100",
    );
    if (amountText === null) return;
    const amount = Number(amountText);
    if (!Number.isInteger(amount)) {
      setMessage("Credit amount must be a whole number.");
      return;
    }
    const response = await apiFetch(`/api/admin/users/${user.id}/credits`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount, reason: "Admin adjustment" }),
    });
    const payload = await response.json();
    if (!response.ok) {
      setMessage(payload.error || "Credit adjustment failed.");
      return;
    }
    setMessage(`Credits updated: ${payload.credits}`);
    await loadAdminData();
  };

  const updateSetting = async (key: string, value: string) => {
    const response = await apiFetch("/api/admin/settings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, value }),
    });
    if (!response.ok)
      setMessage((await response.json()).error || "Unable to save setting.");
    else setMessage("Setting saved.");
  };

  const createCoupon = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const response = await apiFetch("/api/admin/coupons", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: couponForm.name,
        code: couponForm.code,
        type: couponForm.type,
        value: Number(couponForm.value),
        duration: couponForm.duration,
        ...(couponForm.duration === "repeating"
          ? { durationMonths: Number(couponForm.durationMonths) }
          : {}),
        ...(couponForm.maxRedemptions
          ? { maxRedemptions: Number(couponForm.maxRedemptions) }
          : {}),
        ...(couponForm.expiresOn
          ? {
              expiresAt: Math.floor(
                new Date(`${couponForm.expiresOn}T23:59:59`).getTime() / 1000,
              ),
            }
          : {}),
      }),
    });
    const payload = await response.json();
    setBusy(false);
    if (!response.ok) {
      setMessage(
        typeof payload.error === "string"
          ? payload.error
          : "Coupon could not be created.",
      );
      return;
    }
    setMessage("Coupon and promotion code created in Stripe.");
    setCouponForm((current) => ({ ...current, name: "", code: "" }));
    await loadAdminData();
  };

  const deactivateCode = async (couponId: string, codeId: string) => {
    const response = await apiFetch(
      `/api/admin/coupons/${couponId}/deactivate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promotion_code_id: codeId }),
      },
    );
    if (!response.ok) {
      setMessage("Promotion code could not be deactivated.");
      return;
    }
    setMessage("Promotion code deactivated.");
    await loadAdminData();
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFile(event.target.files?.[0] ?? null);
  const errorCsv = result?.failures?.length
    ? `data:text/csv;charset=utf-8,${encodeURIComponent(["row,email,error", ...result.failures.map((item) => [item.row, item.email, item.error].map(csvCell).join(","))].join("\n"))}`
    : "";

  return (
    <div className="admin-page">
      <header className="header">
        <div className="logo">ReachAtlas Admin</div>
        <nav className="nav">
          <Link to="/">Public website</Link>
          <button className="btn-secondary" onClick={() => void logout()}>
            Sign out
          </button>
        </nav>
      </header>
      <main className="admin-main">
        <div className="admin-container">
          <h1>Admin Dashboard</h1>
          <p>
            Manage the shared ReachAtlas database. Imported leads appear in
            customer searches with contact details locked until customers spend
            credits to unlock them.
          </p>
          {message && (
            <div className="info-box" role="status">
              {message}
            </div>
          )}
          <div className="admin-summary" aria-label="Database overview">
            <div><span>Searchable contacts</span><strong>{inventory.contacts.toLocaleString()}</strong></div>
            <div><span>Company records</span><strong>{inventory.companies.toLocaleString()}</strong></div>
            <div><span>Monthly credits</span><strong>5,000</strong></div>
            <div><span>Annual credits upfront</span><strong>60,000</strong></div>
          </div>
          <nav className="admin-jump" aria-label="Admin sections">
            <a href="#import-leads">Import leads</a><a href="#promotions">Annual promotions</a><a href="#admin-settings">Plan settings</a><a href="#admin-users">Users</a><a href="#import-history">Import history</a><a href="#admin-affiliates">Affiliates & payouts</a>
          </nav>
          <AffiliatesAdmin />
          <div className="admin-grid">
            <section className="admin-section admin-import-section" id="import-leads">
              <span className="eyebrow">SHARED DATABASE</span>
              <h2>Import leads</h2>
              <p>Upload contact, company, or combined CSV files, up to 25 MB each. Headers with spaces or underscores are accepted. Preview first, then confirm to publish.</p>
              <div className="admin-template-actions">
                <button className="btn-secondary" onClick={()=>void downloadTemplate('contacts')}>Download contact template</button>
                <button className="btn-secondary" onClick={()=>void downloadTemplate('companies')}>Download company template</button>
              </div>
              <details className="admin-guide"><summary>CSV fields and upload order</summary>
                <p><strong>Contacts:</strong> First Name, Last Name, Email, Company Name (or a domain matching an uploaded company). Optional: Title, Employee Phone, Employee Phone Type, Employee LinkedIn, Email Verification Date, Email Domain, Industry, Employee Count, Revenue, City, Region Code, Country Code.</p>
                <p><strong>Companies:</strong> Company Name and Email Domain are required. Optional: Final Email Domain, URL, Phone, LinkedIn URL, Address, City, State, Zip, Country Code, Industry, Employee Count, Revenue, Services, Home Page Email, Home Page Text, LinkedIn Bio.</p>
                <p>For separate files, upload companies first, then contacts. For contacts, searched_domain is the company join key when supplied; domain is the next fallback, then email_domain or the domain from the email address. The contact email domain is preserved separately even when it differs. Company records enrich matching contacts; a company without contacts does not create an unlockable lead. Combined files can be uploaded directly.</p>
                <p>Contacts deduplicate by email; companies by domain. No-contact placeholders are skipped. Imported verification dates do not automatically mark emails as verified. Additional source columns are retained but are not searchable fields.</p>
              </details>
              <form onSubmit={importCsv}>
                <div className="form-group">
                  <label htmlFor="csv-file">CSV file</label>
                  <input
                    id="csv-file"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(event) => {
                      onFileChange(event);
                      setPreview(null);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="import-mode">Import mode</label>
                  <select
                    id="import-mode"
                    value={mode}
                    onChange={(event) => {
                      setMode(event.target.value);
                      setPreview(null);
                    }}
                  >
                    <option value="add_new_only">Add new only</option>
                    <option value="add_update">Add + update</option>
                    <option value="replace_all">
                      Replace all and archive missing
                    </option>
                  </select>
                </div>
                <p className="fine-print">{mode === 'add_new_only' ? 'Safest for uploading batches: add new records and skip existing emails or domains.' : mode === 'add_update' ? 'Add new records and update matching emails or domains. Blank cells preserve existing values.' : 'Contact files only. Records missing from this file will be archived. Do not use for individual batches.'}</p>
                {mode === "replace_all" && (
                  <div className="form-group">
                    <label htmlFor="confirmation">
                      Type ARCHIVE_MISSING_LEADS
                    </label>
                    <input
                      id="confirmation"
                      value={confirmation}
                      onChange={(event) => setConfirmation(event.target.value)}
                    />
                  </div>
                )}
                <button
                  className="btn-primary"
                  disabled={
                    busy ||
                    (Boolean(preview) && !preview?.validRows) ||
                    (mode === 'replace_all' && preview?.kind === 'companies') ||
                    (mode === 'replace_all' && Boolean(preview?.placeholders)) ||
                    (mode === "replace_all" && !preview && !confirmation) ||
                    (mode === "replace_all" &&
                      Boolean(preview?.failures.length))
                  }
                >
                  {busy
                    ? "Working..."
                    : preview
                      ? "Confirm and import CSV"
                      : "Preview CSV"}
                </button>
              </form>
              {preview && (
                <div className="info-box">
                  <strong>Preview · {preview.kind === 'companies' ? 'Company records' : 'Contact records'}</strong>
                  <p>
                    {preview.validRows} valid of {preview.totalRows} rows ·{" "}
                    {preview.failures.length} invalid. Only the first 20 rows
                    are shown below.
                  </p>
                  <p>{preview.duplicates} duplicate rows skipped · {preview.placeholders} no-contact placeholders skipped · {preview.existingRows} matching records already in the database.</p>
                  {!!preview.unmappedColumns.length && <p>Additional columns retained as source data: {preview.unmappedColumns.join(', ')}. These are not searchable fields.</p>}
                  {preview.kind === 'companies' && <p>These company records will enrich matching contacts. Upload a contact file next to add unlockable leads.</p>}
                  {preview.failures.length > 0 && (
                    <p>
                      Invalid rows will be excluded. Replace all is disabled until they are fixed. Row numbers:{" "}
                      {preview.failures
                        .slice(0, 10)
                        .map((item) => item.row)
                        .join(", ")}
                    </p>
                  )}
                  {preview.failures.slice(0,5).map(item=><p key={item.row}>Row {item.row}: {item.error}</p>)}
                  <div className="leads-table">
                    <table>
                      <thead>
                        <tr>
                          {preview.headers.map((header) => (
                            <th key={header}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.map((row, index) => (
                          <tr key={index}>
                            {preview.headers.map((header) => (
                              <td key={header}>{row[header]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {result && (
                <div className="info-box">
                  <strong>Result</strong>
                  <p>
                    Inserted: {result.inserted} · Updated: {result.updated} ·
                    Skipped: {result.skipped} · Failed: {result.failed}
                  </p>
                  {errorCsv && (
                    <a download="reachatlas-import-errors.csv" href={errorCsv}>
                      Download error CSV
                    </a>
                  )}
                </div>
              )}
            </section>

            <section className="admin-section" id="promotions">
              <h2>Annual promotions</h2>
              <p>
                First-year annual discounts only, capped at 50% ($120). Monthly
                plans cannot use these codes.
              </p>
              <form onSubmit={createCoupon}>
                <div className="form-group">
                  <label htmlFor="coupon-name">Name</label>
                  <input
                    id="coupon-name"
                    value={couponForm.name}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, name: event.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="coupon-code">Promotion code</label>
                  <input
                    id="coupon-code"
                    value={couponForm.code}
                    onChange={(event) =>
                      setCouponForm({
                        ...couponForm,
                        code: event.target.value.toUpperCase(),
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="coupon-type">Discount type</label>
                  <select
                    id="coupon-type"
                    value={couponForm.type}
                    onChange={(event) =>
                      setCouponForm({ ...couponForm, type: event.target.value })
                    }
                  >
                    <option value="percent">Percent off</option>
                    <option value="amount">Amount off (USD)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="coupon-value">Value</label>
                  <input
                    id="coupon-value"
                    type="number"
                    min="0.01"
                    max={couponForm.type === "percent" ? 50 : 120}
                    step="0.01"
                    value={couponForm.value}
                    onChange={(event) =>
                      setCouponForm({
                        ...couponForm,
                        value: event.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="coupon-duration">Duration</label>
                  <select
                    id="coupon-duration"
                    value={couponForm.duration}
                    onChange={(event) =>
                      setCouponForm({
                        ...couponForm,
                        duration: event.target.value,
                      })
                    }
                  >
                    <option value="once">First annual invoice only</option>
                  </select>
                </div>
                {couponForm.duration === "repeating" && (
                  <div className="form-group">
                    <label htmlFor="coupon-months">Duration in months</label>
                    <input
                      id="coupon-months"
                      type="number"
                      min="1"
                      value={couponForm.durationMonths}
                      onChange={(event) =>
                        setCouponForm({
                          ...couponForm,
                          durationMonths: event.target.value,
                        })
                      }
                      required
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="coupon-uses">
                    Max redemptions (optional)
                  </label>
                  <input
                    id="coupon-uses"
                    type="number"
                    min="1"
                    value={couponForm.maxRedemptions}
                    onChange={(event) =>
                      setCouponForm({
                        ...couponForm,
                        maxRedemptions: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="coupon-expiry">Expiry date (optional)</label>
                  <input
                    id="coupon-expiry"
                    type="date"
                    value={couponForm.expiresOn}
                    onChange={(event) =>
                      setCouponForm({
                        ...couponForm,
                        expiresOn: event.target.value,
                      })
                    }
                  />
                </div>
                <button className="btn-primary" disabled={busy}>
                  Create coupon
                </button>
              </form>
              <div className="leads-table">
                <table>
                  <thead>
                    <tr>
                      <th>Coupon</th>
                      <th>Discount</th>
                      <th>Duration</th>
                      <th>Code</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.flatMap((coupon) =>
                      coupon.promotion_codes.map((code) => (
                        <tr key={code.id}>
                          <td>{coupon.name || coupon.id}</td>
                          <td>
                            {coupon.percent_off
                              ? `${coupon.percent_off}%`
                              : `$${((coupon.amount_off || 0) / 100).toFixed(2)}`}
                          </td>
                          <td>{coupon.duration}</td>
                          <td>{code.code}</td>
                          <td>
                            <button
                              className="btn-small"
                              onClick={() =>
                                void deactivateCode(coupon.id, code.id)
                              }
                            >
                              Deactivate
                            </button>
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="admin-section" id="admin-settings">
              <h2>Settings</h2>
              {settings
                .filter((setting) => !setting.key.startsWith("export_cap_"))
                .map((setting) => (
                  <div className="setting-row" key={setting.key}>
                    <label htmlFor={setting.key}>{setting.key === 'credits_monthly_plan' ? 'Monthly plan credits' : setting.key === 'credits_yearly_plan' ? 'Annual plan credits upfront' : setting.key}</label>
                    <input
                      id={setting.key}
                      value={setting.value}
                      onChange={(event) =>
                        setSettings((current) =>
                          current.map((item) =>
                            item.key === setting.key
                              ? { ...item, value: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                    <button className="btn-secondary" onClick={()=>void updateSetting(setting.key, setting.value)}>Save</button>
                  </div>
                ))}
            </section>

            <section className="admin-section" id="admin-users">
              <h2>Users</h2>
              <div className="leads-table">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Credits</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td>{user.plan}</td>
                        <td>{user.status}</td>
                        <td>{user.credits}</td>
                        <td>
                          <button
                            className="btn-small"
                            onClick={() => void adjustCredits(user)}
                          >
                            Adjust credits
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="admin-section" id="import-history">
              <h2>Import history</h2>
              <div className="leads-table">
                <table>
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Admin</th>
                      <th>Mode</th>
                      <th>Inserted</th>
                      <th>Updated</th>
                      <th>Skipped</th>
                      <th>Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!history.length && <tr><td colSpan={7}>No imports yet. Upload a CSV above to populate your database.</td></tr>}
                    {history.map((item, index) => (
                      <tr key={index}>
                        <td>{String(item.filename)}</td>
                        <td>{String(item.admin_email)}</td>
                        <td>{String(item.mode)}</td>
                        <td>{String(item.inserted_count)}</td>
                        <td>{String(item.updated_count)}</td>
                        <td>{String(item.skipped_count)}</td>
                        <td>{String(item.failed_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
